/**
 * The command registry behind the Ctrl/Cmd+K palette. The shell registers navigation;
 * pages register their own actions while mounted:
 *
 *   useRegisterCommands([
 *     { id: 'session.new', title: 'New session', group: 'Sessions', icon: Plus, shortcut: ['n'], perform: openNewSession },
 *   ], [openNewSession]);
 *
 * Ids are unique; registering an id again replaces the earlier command.
 */
import { useEffect } from 'react';
import type { ComponentType } from 'react';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

export interface Command {
  /** Unique, dotted: 'nav.sessions', 'session.new', 'pipeline.pause:<key>'. */
  id: string;
  title: string;
  /** Palette section: 'Navigation', 'Sessions', 'Pipelines', 'General'. */
  group?: string;
  /** One line under the title, for context (a machine, a path). */
  subtitle?: string;
  icon?: ComponentType<{ className?: string }>;
  /** Shown in the palette; global single keys are handled by the shell, not by this field. */
  shortcut?: string[];
  /** Keys pressed one after the other (g then s). */
  shortcutThen?: boolean;
  /** Extra search words. */
  keywords?: string[];
  /** Hidden from the palette when false. */
  enabled?: boolean;
  /** Lower sorts first within its group. Default 100. */
  order?: number;
  perform: () => void | Promise<void>;
}

interface CommandStore {
  commands: Record<string, Command>;
  paletteOpen: boolean;
  /** Text to prefill when the palette opens. */
  paletteQuery: string;
}

export const useCommandStore = create<CommandStore>()(() => ({ commands: {}, paletteOpen: false, paletteQuery: '' }));

/** Register commands; returns a function that removes exactly these. */
export function registerCommands(cmds: Command[]): () => void {
  useCommandStore.setState((s) => {
    const commands = { ...s.commands };
    for (const c of cmds) commands[c.id] = c;
    return { commands };
  });
  return () =>
    useCommandStore.setState((s) => {
      const commands = { ...s.commands };
      for (const c of cmds) if (commands[c.id] === c) delete commands[c.id];
      return { commands };
    });
}

/** Register commands while the calling component is mounted. Re-registers when `deps` change. */
export function useRegisterCommands(cmds: Command[], deps: unknown[] = []): void {
  useEffect(() => registerCommands(cmds), deps);
}

/** Every enabled command, sorted by group order then `order` then title. */
export function useCommands(): Command[] {
  return useCommandStore(
    useShallow((s) =>
      Object.values(s.commands)
        .filter((c) => c.enabled !== false)
        .sort((a, b) => (a.order ?? 100) - (b.order ?? 100) || a.title.localeCompare(b.title)),
    ),
  );
}

export function getCommand(id: string): Command | undefined {
  const c = useCommandStore.getState().commands[id];
  return c && c.enabled !== false ? c : undefined;
}

/** Run a registered command by id. Returns false when it is not registered (or disabled). */
export function runCommand(id: string): boolean {
  const c = getCommand(id);
  if (!c) return false;
  void c.perform();
  return true;
}

export function openPalette(query = ''): void {
  useCommandStore.setState({ paletteOpen: true, paletteQuery: query });
}
export function closePalette(): void {
  useCommandStore.setState({ paletteOpen: false, paletteQuery: '' });
}
export function togglePalette(): void {
  const open = useCommandStore.getState().paletteOpen;
  if (open) closePalette();
  else openPalette();
}
export const usePaletteOpen = (): boolean => useCommandStore((s) => s.paletteOpen);
